/**
 * The one filter row that scopes every number on an analytics page (spec §7.2). It lives in the URL
 * (typed search params), is resolved once into concrete UTC windows, and the same object feeds
 * every widget, so the numbers always agree.
 */
import { z } from 'zod';
import { filterParam } from '@/lib/url-state';

export const PERIODS = ['today', 'yesterday', '7d', '30d', 'mtd', 'last_month', 'custom'] as const;
export type Period = (typeof PERIODS)[number];

export const PERIOD_LABELS: Record<Period, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  mtd: 'Month to date',
  last_month: 'Last month',
  custom: 'Custom range',
};

export const COMPARES = ['previous', 'last_month', 'none'] as const;
export type Compare = (typeof COMPARES)[number];

export const COMPARE_LABELS: Record<Compare, string> = {
  previous: 'Previous period',
  last_month: 'Same period last month',
  none: 'No comparison',
};

export const ROUND_TYPES = ['real', 'free', 'all'] as const;
export type RoundType = (typeof ROUND_TYPES)[number];

export const ROUND_TYPE_LABELS: Record<RoundType, string> = {
  real: 'Real-money rounds',
  free: 'Free rounds',
  all: 'All rounds',
};

/** The trend card's metric switch (spec §7.1). */
export const TREND_METRICS = ['ggr', 'turnover', 'rounds', 'activePlayers', 'rtp'] as const;
export type TrendMetric = (typeof TREND_METRICS)[number];

/** A day window is capped at 92 days (spec §7.4.3); longer custom ranges are refused before any request. */
export const MAX_DAY_WINDOW = 92;

// Arrays arrive JSON-parsed from the URL (`?operatorIds=[1,2]`); a lone value is accepted too.
const list = <T extends z.ZodType>(item: T) =>
  z
    .preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(item).max(200))
    .optional()
    .catch(undefined);

export const analyticsFilterShape = {
  period: filterParam.enum(PERIODS),
  from: filterParam.date(),
  to: filterParam.date(),
  compare: filterParam.enum(COMPARES),
  operatorIds: list(z.coerce.number().int().positive()),
  brandIds: list(z.coerce.string().trim().min(1).max(100)),
  gameIds: list(z.coerce.number().int().positive()),
  titleKeys: list(z.coerce.string().trim().min(1).max(200)),
  currencies: list(z.coerce.string().trim().min(1).max(8)),
  roundType: filterParam.enum(ROUND_TYPES),
  includeTestPlayers: filterParam.boolean(),
};

export const analyticsSearchSchema = z.object(analyticsFilterShape);
export type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>;

export type Grain = 'hour' | 'day';

export interface Window {
  /** `YYYY-MM-DD`, inclusive, UTC. */
  from: string;
  to: string;
}

export interface ResolvedFilters extends Window {
  period: Period;
  /** Today and yesterday are shown by the hour, everything else by the day. */
  grain: Grain;
  compare: Compare;
  compareWindow: Window | null;
  operatorIds: number[];
  brandIds: string[];
  gameIds: number[];
  titleKeys: string[];
  currencies: string[];
  roundType: RoundType;
  includeTestPlayers: boolean;
  /** Days in the window, both ends included. */
  days: number;
  /** A custom range the server would refuse (> 92 days, or `to` before `from`). */
  invalid: string | null;
}

// ---- dates, all in UTC --------------------------------------------------------------------------

const DAY_MS = 86_400_000;
export const isoDay = (d: Date) => d.toISOString().slice(0, 10);
export const parseDay = (s: string) => new Date(`${s}T00:00:00Z`);
export const addDays = (s: string, n: number) => isoDay(new Date(parseDay(s).getTime() + n * DAY_MS));
export const daysBetween = (from: string, to: string) =>
  Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / DAY_MS) + 1;

const monthStart = (s: string) => `${s.slice(0, 7)}-01`;
const daysInMonth = (year: number, month0: number) => new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();

/** The same calendar dates one month earlier, clamped to that month's last day. */
export function shiftMonth(s: string, months: number): string {
  const d = parseDay(s);
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
  const day = Math.min(d.getUTCDate(), daysInMonth(target.getUTCFullYear(), target.getUTCMonth()));
  return isoDay(new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), day)));
}

export function periodWindow(period: Period, now: Date, custom?: Partial<Window>): Window {
  const today = isoDay(now);
  const yesterday = addDays(today, -1);
  switch (period) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday':
      return { from: yesterday, to: yesterday };
    case '7d':
      return { from: addDays(yesterday, -6), to: yesterday };
    case 'mtd':
      return { from: monthStart(today), to: today };
    case 'last_month': {
      const start = shiftMonth(monthStart(today), -1);
      return { from: start, to: addDays(monthStart(today), -1) };
    }
    case 'custom':
      if (custom?.from && custom.to) return { from: custom.from, to: custom.to };
      return { from: addDays(yesterday, -29), to: yesterday };
    case '30d':
    default:
      return { from: addDays(yesterday, -29), to: yesterday };
  }
}

export function compareWindow(window: Window, compare: Compare): Window | null {
  if (compare === 'none') return null;
  if (compare === 'last_month') return { from: shiftMonth(window.from, -1), to: shiftMonth(window.to, -1) };
  const length = daysBetween(window.from, window.to);
  return { from: addDays(window.from, -length), to: addDays(window.from, -1) };
}

export const DEFAULT_PERIOD: Period = '30d';

/** Window caps per grain (spec §7.4.3), in days. */
export const MAX_DAYS_BY_GRAIN = { hour: 14, day: MAX_DAY_WINDOW, week: 53 * 7, month: 25 * 31 } as const;

export function resolveFilters(
  search: AnalyticsSearch,
  now: Date = new Date(),
  maxDays: number = MAX_DAY_WINDOW,
): ResolvedFilters {
  const period = search.period ?? (search.from && search.to ? 'custom' : DEFAULT_PERIOD);
  const window = periodWindow(period, now, search);
  const days = daysBetween(window.from, window.to);
  const invalid =
    days < 1
      ? 'The end date is before the start date.'
      : days > maxDays
        ? maxDays === MAX_DAY_WINDOW
          ? `Choose ${MAX_DAY_WINDOW} days or fewer here; Explore by month covers longer ranges.`
          : `Choose ${maxDays} days or fewer for this grain.`
        : null;
  const compare = search.compare ?? 'previous';
  return {
    ...window,
    period,
    grain: period === 'today' || period === 'yesterday' ? 'hour' : 'day',
    compare,
    compareWindow: compareWindow(window, compare),
    operatorIds: search.operatorIds ?? [],
    brandIds: search.brandIds ?? [],
    gameIds: search.gameIds ?? [],
    titleKeys: search.titleKeys ?? [],
    currencies: search.currencies ?? [],
    roundType: search.roundType ?? 'real',
    includeTestPlayers: search.includeTestPlayers ?? false,
    days,
    invalid,
  };
}

/** The filter part of every analytics request (`docs/analytics-api-contract.md`). */
export function filterParams(
  f: ResolvedFilters,
  window: Window = f,
): Record<string, string | number | string[] | number[]> {
  const params: Record<string, string | number | string[] | number[]> = {
    from: window.from,
    to: window.to,
    roundType: f.roundType,
    includeTestPlayers: String(f.includeTestPlayers),
  };
  if (f.operatorIds.length) params.operatorIds = f.operatorIds;
  if (f.brandIds.length) params.brandIds = f.brandIds;
  if (f.gameIds.length) params.gameIds = f.gameIds;
  if (f.titleKeys.length) params.titleKeys = f.titleKeys;
  if (f.currencies.length) params.currencies = f.currencies;
  return params;
}

const shortDay = (s: string, withYear: boolean) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(parseDay(s));

/** `27 Aug – 25 Sep`, `26 Sep`; years appear when the window crosses one or isn't this year's. */
export function describeWindow(w: Window, now: Date = new Date()): string {
  const year = String(now.getUTCFullYear());
  const withYear = w.from.slice(0, 4) !== w.to.slice(0, 4) || w.to.slice(0, 4) !== year;
  return w.from === w.to ? shortDay(w.from, withYear) : `${shortDay(w.from, withYear)} – ${shortDay(w.to, withYear)}`;
}

/** How a delta names its baseline: `vs 28 Jul – 26 Aug`. */
export function compareLabel(f: ResolvedFilters): string {
  return f.compareWindow ? `vs ${describeWindow(f.compareWindow)}` : '';
}

/** Every UTC day in the window, for filling empty buckets. */
export function eachDay(w: Window): string[] {
  const out: string[] = [];
  for (let d = w.from; d <= w.to; d = addDays(d, 1)) out.push(d);
  return out;
}

/**
 * Search params that differ from the defaults: keeps shared links short. The period always stays:
 * each browser remembers its own (stored-period.ts), so a link names it to mean the same view for
 * everyone.
 */
export function cleanSearch(search: AnalyticsSearch): AnalyticsSearch {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (key === 'compare' && value === 'previous') continue;
    if (key === 'roundType' && value === 'real') continue;
    if (key === 'includeTestPlayers' && value === false) continue;
    out[key] = value;
  }
  if (out.period !== 'custom') {
    delete out.from;
    delete out.to;
  }
  return out;
}
